// Single source of truth for the on-disk team format. A team composes
// references to a car + 1-2 drivers and owns its own livery. Refs are
// by id (filename-derived, like tracks) so a single car spec is
// reusable across teams without duplication.
//
// `entries` is a list of (driverId, carNumber) pairs rather than
// parallel arrays — keeps the number bound to the driver and leaves
// room for endurance fields (3+ drivers per car) without a schema
// break.
//
// Forward compatibility: additive fields only; loadTeam() backfills
// missing pieces. resolveTeam() is a separate pass that walks the
// refs against an already-loaded {cars, drivers} catalog and reports
// any missing ids — the roster picker can warn the same way
// loadTrack warns on formatMismatch.

export const FORMAT_VERSION = 1;

export function newEmptyTeam() {
  return {
    formatVersion: FORMAT_VERSION,
    name: '',
    shortName: '',                                 // e.g. "RDL"
    livery: { primary: '#003366', accent: '#ffcc00' },
    carId: null,                                   // ref → cars/<carId>.car.json
    entries: [],                                   // [{ driverId, carNumber }]
    metadata: { createdAt: null, modifiedAt: null },
  };
}

export function loadTeam(data) {
  if (!data || typeof data !== 'object') {
    throw new Error('Not a valid team file: not an object');
  }
  if (typeof data.formatVersion !== 'number') {
    throw new Error('Not a valid team file: missing formatVersion');
  }
  if (typeof data.name !== 'string' || data.name.trim() === '') {
    throw new Error('Not a valid team file: missing name');
  }

  const merged = newEmptyTeam();
  Object.assign(merged, data);

  merged.livery = { ...newEmptyTeam().livery, ...(data.livery ?? {}) };
  if (typeof merged.shortName !== 'string') merged.shortName = '';
  if (merged.carId !== null && typeof merged.carId !== 'string') merged.carId = null;

  // Entries normalize to a clean array of well-shaped objects. Drop
  // anything malformed silently — a team file with junk rows is more
  // useful loaded than rejected; resolveTeam() will surface missing
  // refs separately.
  const rawEntries = Array.isArray(data.entries) ? data.entries : [];
  merged.entries = rawEntries
    .filter(e => e && typeof e.driverId === 'string')
    .map(e => ({
      driverId: e.driverId,
      carNumber: typeof e.carNumber === 'number' ? e.carNumber : null,
    }));

  if (!merged.metadata) merged.metadata = { createdAt: null, modifiedAt: null };

  return {
    team: merged,
    formatMismatch: data.formatVersion > FORMAT_VERSION ? data.formatVersion : null,
  };
}

// Resolve a team's id refs against an already-loaded catalog. Returns
// `{ resolved, missingRefs }` where `resolved` mirrors `team` shape but
// with `car` (object|null) and `entries[i].driver` (object|null), and
// `missingRefs` lists ids that didn't resolve. The original `team` is
// not mutated.
//
// `catalog.cars` and `catalog.drivers` are id → object maps (the loader
// pages build them from listCars()/listDrivers()).
export function resolveTeam(team, { cars = {}, drivers = {} } = {}) {
  const missingRefs = [];
  const car = team.carId ? cars[team.carId] ?? null : null;
  if (team.carId && !car) missingRefs.push({ kind: 'car', id: team.carId });

  const entries = team.entries.map(e => {
    const driver = drivers[e.driverId] ?? null;
    if (!driver) missingRefs.push({ kind: 'driver', id: e.driverId });
    return { ...e, driver };
  });

  return {
    resolved: { ...team, car, entries },
    missingRefs,
  };
}
